import { Trans, t } from '@lingui/macro';
import hotkeys from 'hotkeys-js';
import { ChevronDown, ChevronUp, Plus, Search, Star, X } from 'lucide-react';
import { RefObject, useEffect, useReducer, useState, useMemo, useRef } from 'react';
import { useHistory, useLocation } from '../../hooks/useRouterCompat';
import { useAppUIContext } from '../../context/AppUIContext';
import { Room } from '../../models/Room';
import { RoomCall } from '../../models/RoomCall';
import { User } from '../../models/User';
import { Workspace } from '../../models/Workspace';
import { Fire } from '../../services/FireClient';
import { My } from '../../services/My';
import { MyNotifs } from '../../services/MyNotifs';
import { LocalStorage } from '../../services/Utils/LocalStorage';
import CreatePopover from '../components/CreatePopover';
import { ProfilePart } from '../components/ProfilePart';
import RoomItem from './LeftMenuRoomItem';
import { useMessageContext } from '../main/NotificationModal';
import UserSelectModal from '../components/UserSelectModal';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { cn } from '../../lib/utils';
var PATH_FAVORITES = '/favorites';
var PATH_EXT_SHARES = '/shares';

const LeftMenuRooms: React.FC = () => {
  const { workspaces, rooms, setRooms, setIsMobileMenuOpen, activeCalls, roomsLoaded, setRoomsLoaded } = useAppUIContext();
  const [showMenu, setShowMenu] = useState<{ myRooms: boolean, one2one: boolean, showHiddenWSGroup: string[] }>({ myRooms: true, one2one: true, showHiddenWSGroup: [] });
  const [currentPath, setCurrentPath] = useState<string>();
  const [pathEnd, setPathEnd] = useState<string>('');
  const [filteredRooms, setFilterdRooms] = useState<Room[]>([]);
  // rooms context를 초기값으로 사용 (모바일에서 Sheet 열릴 때 이미 데이터가 있을 수 있음)
  const [allRooms, setAllRooms] = useState<Room[]>(() => rooms || []);
  const [filterKey, setFilterKey] = useState<string | null>();
  const [workspaceRooms, setWorkspaceRooms] = useState(new Map<Workspace, Room[]>());
  const [one2oneRooms, setOne2OneRooms] = useState<Room[]>([]);
  const [personalRooms, setPersonalRooms] = useState<Room[]>([]);
  const [notifsByRoom] = useState(new Map<string, number>()); // roomId, nrNotifs
  const [notifsByRoomNoPush] = useState(new Map<string, number>()); // roomId, nrNotifs
  const history = useHistory();
  const location = useLocation();
  const [_, forceUpdate] = useReducer((x) => x + 1, 0);
  const { notification } = useMessageContext();
  const [isFavorite, setIsFavorite] = useState(LocalStorage.get('last-is-favorite') === 'favorite' ? true : false);
  const [showUserSelectModal, setShowUserSelectModal] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    hotkeys('f', function (event, handler) {
      event.preventDefault();
      inputRef.current?.focus();
    });

    // 방 목록은 useInitialNavigation에서 이미 구독하고 있음
    // 여기서는 AppUIContext의 rooms를 사용하여 UI 업데이트만 처리



    // 알림 업데이트는 App.tsx에서 Firestore 구독으로 관리됨
    // 여기서는 MyNotifs 변경 이벤트만 구독하여 UI 업데이트
    let unsubNotifs = MyNotifs.subscribeToNotificationUpdates((notifs) => {
      notifsByRoom.clear();
      notifsByRoomNoPush.clear();
      MyNotifs.NotificationsByRoom.forEach((notifs, roomId) => {
        const filteredCount = notifs.filter(n => n.isNew && n.type != 'RoomAdded' && n.type != 'CallStart').length;
        notifsByRoom.set(roomId, filteredCount);
        notifsByRoomNoPush.set(roomId, notifs.filter(n => n.isNew && n.type != 'RoomAdded' && n.type != 'CallStart' && My.RoomSettings.get(roomId)?.disablePushNotifications).length);
      });
      forceUpdate();
    });
    setCurrentPath(window.location.pathname.replace('/room/', ''));
    setPathEnd([PATH_FAVORITES, PATH_EXT_SHARES].find(p => window.location.pathname?.endsWith(p)) || '');
    function handler(event: MessageEvent) {
      if (event.data?.type === 'go-to-room' && event.data.roomId) {
        history.replace('/room/' + event.data.roomId);
      }
    }
    window.addEventListener('message', handler);

    My.updateRoomSettings();
    const unsubRoomSettings = My.subscribeToRoomSettingUpdates(() => { forceUpdate() });
    return () => {
      unsubNotifs();
      unsubRoomSettings();
      hotkeys.unbind('f');
      window.removeEventListener('message', handler);
    }
  }, [])

  // Track location changes (react-router v6 pattern)
  useEffect(() => {
    setCurrentPath(location.pathname.replace('/room/', ''));
    setPathEnd([PATH_FAVORITES, PATH_EXT_SHARES].find(p => location.pathname?.endsWith(p)) || '');
  }, [location.pathname]);

  // rooms context가 변경될 때 UI 업데이트 (useInitialNavigation에서 rooms가 설정됨)
  // 모바일에서는 Sheet가 열릴 때 이 컴포넌트가 마운트되므로,
  // 마운트 시점에 이미 rooms가 설정되어 있는 경우도 처리해야 함
  useEffect(() => {
    if (rooms && rooms.length >= 0) {
      My.setRooms(rooms);
      setAllRooms(rooms);
      // changeRoomLists는 isFavorite, filterKey를 클로저로 캡처하므로
      // 직접 로직을 여기서 실행
      let roomList = [...rooms];
      if (filterKey) {
        roomList = roomList.filter(r => r.name.toLocaleLowerCase().includes(filterKey.toLocaleLowerCase()));
      }
      if (isFavorite) {
        roomList = roomList.filter(r => (r.visibility === 'personal' || My.RoomSettings.get(r.id)?.favoriteRoomInMyList));
      }

      const _one2oneRooms: Room[] = [];
      const _personalRooms: Room[] = [];
      const _otherRooms: Room[] = [];

      roomList.forEach(r => {
        if (r.visibility === 'personal') {
          _personalRooms.push(r);
        } else if (r.isOneToOne) {
          _one2oneRooms.push(r);
        } else {
          _otherRooms.push(r);
        }
      });

      setFilterdRooms(Room.sortRoomsByName(_otherRooms));
      setOne2OneRooms(Room.sortRoomsByDate(_one2oneRooms));
      setPersonalRooms(_personalRooms);
    }
  }, [rooms, workspaces, isFavorite, filterKey]);

  useEffect(() => {
    let map = new Map<Workspace, Room[]>();
    workspaces.forEach(w => map.set(w, []));
    const guestWS = new Workspace("", t`Guest`, My.user);

    filteredRooms.forEach(r => {
      if (r.workspaceId) {
        const ws = workspaces.find(w => w.id == r.workspaceId) ?? guestWS;
        if (!map.has(ws)) {
          map.set(ws, []);
        }
        map.get(ws)!.push(r);
      }
    })

    const privateRooms = filteredRooms.filter(r => !r.workspaceId);
    if (privateRooms.length > 0) {
      map.set(new Workspace("", t`Private`, My.user), privateRooms);
    }
    setWorkspaceRooms(map);
  }, [workspaces, filteredRooms])
  useEffect(() => {
    if (notification) {
      // menuRef?.current?.close();
    }
  }, [notification]);


  useEffect(() => {
    changeRoomLists(allRooms, filterKey);
    LocalStorage.set('last-is-favorite', isFavorite ? "favorite" : "");
  }, [isFavorite]);

  // useMemoGetRoomList를 useState/useEffect 선언 바로 아래로 이동


  function changeRoomLists(roomList: Room[], key?: string | null) {
    let _one2oneRooms: Room[] = [];
    let _personalRooms: Room[] = [];
    let _otherRooms: Room[] = [];
    if (key) {
      roomList = roomList.filter(r => r.name.toLocaleLowerCase().includes(key.toLocaleLowerCase()));
    }
    if (isFavorite) {
      roomList = roomList.filter(r => (r.visibility === 'personal' || My.RoomSettings.get(r.id)?.favoriteRoomInMyList));
    }
    roomList.forEach(r => {
      if (r.visibility === 'personal') {
        _personalRooms.push(r);
      } else if (r.isOneToOne)
        _one2oneRooms.push(r);
      else
        _otherRooms.push(r);
    })

    setFilterdRooms(Room.sortRoomsByName(_otherRooms));
    setOne2OneRooms(Room.sortRoomsByDate(_one2oneRooms));
    setPersonalRooms(_personalRooms);
    setFilterKey(key);
  }
  const useMemoGetRoomList = useMemo(() => {
    const _wsRooms = Array.from(workspaceRooms);
    if (_wsRooms.length == 0) {
      return <div className='px-6 py-4 text-gray-400 text-center'><Trans>No rooms or workspaces</Trans></div>;
    } else {
      return _wsRooms.map(([ws, wsRooms], index) => {
        const isLast = index === _wsRooms.length - 1;
        if (wsRooms.length == 0 && (!!filterKey || isFavorite)) {
          return null;
        } else {
          return <div key={`${ws.id}-${ws.name}`} className={isLast ? "" : "mb-4"}>
            {!!ws.id ?
              <div
                className={cn(
                  "flex items-center gap-2 px-4 py-2 mx-2 rounded-lg font-bold text-black hover:text-gray-900 hover:bg-gray-100 cursor-pointer transition-colors select-none",
                  currentPath === `/ws/${ws.id}` && "text-primary bg-primary/10 hover:bg-primary/20 hover:text-primary"
                )}
                onClick={() => {
                  history.push(`/ws/${ws.id}`);
                  if (window.innerWidth < 1200) {
                    setIsMobileMenuOpen(false);
                  }
                }}
              >
                <img className="w-4 h-4 opacity-70" src="/assets/spc/groups-2.svg" alt="workspace" />
                <span className="truncate">{ws.name}</span>
              </div>
              :
              <div className="flex items-center gap-2 px-4 py-2 mx-2 font-bold text-black select-none">
                <img className="w-4 h-4 opacity-60" src="/assets/spc/groups.svg" alt="group" />
                <span className="truncate">{ws.name}</span>
              </div>
            }
            {wsRooms.length == 0 && <div className='px-8 py-2 text-xs text-gray-400 italic'><Trans>No joined rooms</Trans></div>}
            <div className="space-y-0.5 mt-0.5">
              {wsRooms.filter(r => !My.RoomSettings.get(r.id)?.hideRoomInMyList).map(room => roomItem(room))}
              {getHiddenRoomPart(ws, wsRooms.filter(r => My.RoomSettings.get(r.id)?.hideRoomInMyList))}
            </div>
          </div >;
        }
      });
    }
  }, [workspaceRooms, filterKey, isFavorite, currentPath, showMenu, notification, _]);


  // useMemoGetRoomList를 컴포넌트 상단으로 이동


  return (
    <>
      <div className='flex flex-col w-full h-full bg-white rounded-none md:rounded-panel overflow-hidden'>
        <div className="shrink-0 p-4 pb-2">
          <div className="mb-4">
            <ProfilePart />
          </div>

          {(isFavorite || allRooms.length > 10) &&
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  ref={inputRef}
                  placeholder={t`Filter rooms`}
                  value={filterKey || ''}
                  onChange={(e) => filterRooms(e.target.value)}
                  variant="basic"
                />
                {filterKey && (
                  <button onClick={() => filterRooms(null)} className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 rounded-full hover:bg-gray-200 transition-colors">
                    <X className="h-3 w-3 text-gray-400" />
                  </button>
                )}
              </div>
              <Button
                variant="icon-box"
                size="icon"
                className={cn(
                  "transition-colors",
                  isFavorite ? "text-yellow-500" : ""
                )}
                onClick={() => setIsFavorite(!isFavorite)}
              >
                <Star className={cn(isFavorite && "fill-current")} />
              </Button>
            </div>}
        </div>
        <div className="flex-1 overflow-y-auto custom-scrollbar pb-4">
          <div className="mb-6 flex flex-col gap-1">
            <div className="flex items-center justify-between px-4 pt-1 group">
              {getMenuTitle(t`Rooms`, showMenu.myRooms, () => setShowMenu(p => ({ ...p, myRooms: !p.myRooms })))}
              <div className="flex items-center gap-1">
                <Button variant="icon-box" size="icon" onClick={() => changePathEnd('search')}>
                  <Search />
                </Button>
                <CreatePopover />
              </div>
            </div>

            <div className='space-y-0.5'>
              {showMenu.myRooms && <>
                {personalRooms.map(room => <div className="mb-1" key={room.id}>{roomItem(room)}</div>)}
                {useMemoGetRoomList}
              </>}
            </div>
          </div>

          {one2oneRooms.length > 0 && <>
            <div className="border-b border-gray-100 mx-6 my-4" />
            <div className="mb-4">
              <div className="flex items-center justify-between px-4 pt-2 group">
                {getMenuTitle(t`One-to-one`, showMenu.one2one, () => setShowMenu(p => ({ ...p, one2one: !p.one2one })), () => setShowUserSelectModal(true))}
              </div>
              <div className='space-y-0.5 mt-1'>
                {showMenu.one2one && getOne2OneList()}
              </div>
            </div>
          </>}
        </div>
      </div>
      <UserSelectModal
        show={showUserSelectModal}
        title={t`1:1 Invite`}
        onClose={() => setShowUserSelectModal(false)}
        onSelect={createOneToOneRoom}
        users={getUniqueUsers()}
      />
    </>
  );

  function getOne2OneList() {
    return <>
      {one2oneRooms.filter(r => !My.RoomSettings.get(r.id)?.hideRoomInMyList).map(room => roomItem(room))}
      {getHiddenRoomPart(null, one2oneRooms.filter(r => My.RoomSettings.get(r.id)?.hideRoomInMyList))}
    </>
  }

  function filterRooms(key?: string | null) {
    changeRoomLists(allRooms, key);
  }

  function roomItem(room: Room) {
    let rc = activeCalls.get(room.id);
    let nrOfNotifs = notifsByRoom.get(room.id) ?? 0;
    let isSelected = currentPath?.startsWith(room.id) ?? false;
    let theOtherUser = Room.getOtherOne2OneUser(room, My.user.id);
    let disabledPush = My.RoomSettings.get(room.id)?.disablePushNotifications ?? false;
    const meInTheCall = !!rc?.userIds.find(id => id === My.user.id);

    // menuRef is RefObject<HTMLDivElement> | null.
    // We can't call .close() on a div.
    // But wait, in the new LeftMenu.tsx, we passed null.
    // So menuRef is null.
    // If we want to close the menu, we need a way to communicate with the parent LeftMenu or the Drawer/Sheet if we used one.
    // But currently LeftMenuRooms just renders a list.
    // If the menu is a Sidebar that is always open on desktop and a Drawer on mobile, we need to know if we are on mobile.
    // For now, let's just pass a no-op or handle it later.
    // The user said "Left menu is completely broken", so visual is priority.
    // I will pass undefined for now or just not pass it if I haven't updated RoomItem definition yet.
    // Actually, I need to update RoomItem definition first to accept it.

    // Let's just update RoomItem to NOT use useAppUIContext and NOT try to close menu for now.
    // I will revert the plan to pass prop, and just fix the lint error in RoomItem first.
    return <div key={room.id}>
      <RoomItem room={room} roomCall={rc} nrOfNotifs={nrOfNotifs} isSelected={isSelected} theOtherUser={theOtherUser} meInTheCall={meInTheCall} pathEnd={pathEnd} disabledPush={disabledPush} />
    </div>
  }

  function getHiddenRoomPart(ws: Workspace | null, rooms: Room[]) {
    const listId = ws ? ws.id + ws.name : 'one2one';

    if (rooms.length == 0)
      return <div key={`hidden-${listId}`}></div>;

    let nrOfNotifs = 0;
    let nrOfNotifsNoPush = 0;
    let nrOfNotifsPush = 0;
    rooms.forEach(r => nrOfNotifs += notifsByRoom.get(r.id) ?? 0);
    rooms.forEach(r => nrOfNotifsNoPush += notifsByRoomNoPush.get(r.id) ?? 0);
    nrOfNotifsPush = nrOfNotifs - nrOfNotifsNoPush;
    const showGroup = !!filterKey || showMenu.showHiddenWSGroup.includes(listId);
    const onClick = () => {
      setShowMenu(p => {
        const isCurrentlyShown = p.showHiddenWSGroup.includes(listId);
        if (isCurrentlyShown) {
          return { ...p, showHiddenWSGroup: p.showHiddenWSGroup.filter(name => name !== listId) };
        } else {
          return { ...p, showHiddenWSGroup: [...p.showHiddenWSGroup, listId] };
        }
      });
    }

    return <div className="pl-2 mt-1" key={`hidden-${listId}`}>
      {!isFavorite &&
        <div
          onClick={onClick}
          className="flex items-center gap-2 px-3 py-1.5 mx-2 text-xs font-medium text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded-md cursor-pointer select-none transition-colors"
        >
          {showGroup ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          <span><Trans>{rooms.length} hidden</Trans></span>
          {!!nrOfNotifs && (
            <div className="ml-auto flex gap-1 pointer-events-none">
              {nrOfNotifsNoPush > 0 && <Badge variant="notification" className="bg-gray-400">{nrOfNotifsNoPush}</Badge>}
              {nrOfNotifsPush > 0 && <Badge variant="notification" className="">{nrOfNotifsPush}</Badge>}
            </div>
          )}
        </div>
      }
      {showGroup && <div className="pl-3 border-l-2 border-gray-100 ml-5 my-1 space-y-0.5">
        {rooms.map(room => roomItem(room))}
      </div>
      }
    </div>
  }

  function getMenuTitle(title: string, expanded: boolean, onClickHandler: () => void, onAdd?: (e: any) => void) {
    return (
      <div
        className="flex items-center flex-1 cursor-pointer select-none py-1"
        onClick={onClickHandler}
        data-cy="room"
      >
        <div className="flex items-center gap-1.5 text-base font-bold text-gray-800 uppercase tracking-wider hover:text-gray-800 transition-colors">
          {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />}
          {title}
        </div>
        {onAdd && (
          <Button
            variant="icon-box"
            size="icon"
            className="ml-auto"
            onClick={(e) => { e.stopPropagation(); onAdd(e); }}
          >
            <Plus />
          </Button>
        )}
      </div>
    );
  }

  function getUniqueUsers(): User[] {
    const usersMap = new Map<string, User>();
    const existingUserIds = new Set<string>();

    one2oneRooms.forEach(room => {
      room.users.forEach(user => {
        if (user.id !== My.user.id) {
          existingUserIds.add(user.id);
        }
      });
    });

    allRooms.forEach(room => {
      room.users.forEach(user => {
        if (user.id !== My.user.id && !usersMap.has(user.id) && !existingUserIds.has(user.id)) {
          usersMap.set(user.id, user);
        }
      });
    });
    return Array.from(usersMap.values());
  }

  async function createOneToOneRoom(user: User) {
    const existingRoom = one2oneRooms.find(r => r.userIds.includes(user.id) && r.userIds.includes(My.user.id) && r.userIds.length === 2);
    if (existingRoom) {
      history.push('/room/' + existingRoom.id);
      setShowUserSelectModal(false);
      return;
    }

    const room = new Room(user.name, My.user.id);
    room.isOneToOne = true;
    room.visibility = 'unlisted';
    Room.addUser(room, My.user);
    Room.addUser(room, user);

    await Fire.saveRoom(room);
    history.push('/room/' + room.id);
    setShowUserSelectModal(false);
  }

  function changePathEnd(end: string) {
    if (end == 'search') {
      history.push(`/search`);
    } else {
      const roomId = currentPath?.split('/')[0];
      history.replace(`/room/${roomId}${end}`);
    }
    // menuRef?.current?.close();
  }

  function segmentMenu() {
    if (history.location.pathname.startsWith('/room/')) {
      return <div className='no-border'>
        <div className='left-tab-menu'>
        </div>
      </div >
    }
  }

};

export default LeftMenuRooms;
